import { useEffect, useMemo, useState } from 'react'
import GameSwitcherAppItem from '@components/main/GameSwitcherAppItem'
import { getCurrentMainHomeUrl } from '@routing/subdomains'
import { useAuthSession } from '@stores/authStore'
import { useGameFavoritesStore } from '@stores/gameFavoritesStore'
import { type GameApp } from '@app-types/api/auth'

interface GameSwitcherPanelProps {
  onClose: () => void
}

function GameSwitcherPanel({ onClose }: GameSwitcherPanelProps) {
  const session = useAuthSession()
  const apps = useMemo(() => session?.apps ?? [], [session?.apps])
  const currentApp = session?.currentApp
  const favoriteAppIds = useGameFavoritesStore((state) => state.favoriteAppIds)
  const loadFavorites = useGameFavoritesStore((state) => state.loadFavorites)
  const resetFavorites = useGameFavoritesStore((state) => state.resetFavorites)
  const [search, setSearch] = useState('')
  const userId = session?.user?.id ?? null
  const csrfToken = session?.csrfToken ?? null
  const mainHomeUrl = getCurrentMainHomeUrl()

  useEffect(() => {
    if (userId === null || csrfToken === null) {
      resetFavorites()
      return
    }

    void loadFavorites(userId, csrfToken)
  }, [csrfToken, loadFavorites, resetFavorites, userId])

  const filteredApps = useMemo(() => {
    const query = search.trim().toLowerCase()

    if (!query) {
      return apps
    }

    return apps.filter((app) => {
      return (
        app.name.toLowerCase().includes(query) ||
        app.slug.toLowerCase().includes(query)
      )
    })
  }, [apps, search])
  const favoriteApps = useMemo(() => {
    const appsById = new Map(filteredApps.map((app) => [app.id, app]))

    return favoriteAppIds
      .map((appId) => appsById.get(appId))
      .filter((app): app is GameApp => Boolean(app))
  }, [favoriteAppIds, filteredApps])
  const otherApps = useMemo(() => {
    const favoriteIds = new Set(favoriteAppIds)

    return filteredApps.filter((app) => !favoriteIds.has(app.id))
  }, [favoriteAppIds, filteredApps])

  return (
    <div className="absolute top-full left-0 z-30 w-full border-b border-slate-200 bg-white shadow-lg shadow-slate-950/10 dark:border-zinc-800 dark:bg-zinc-900 dark:shadow-black/30">
      <div className="grid max-h-[min(520px,calc(100svh-4rem))] grid-cols-[15rem_minmax(0,1fr)] overflow-hidden py-4 pr-5 max-[820px]:grid-cols-1 max-[820px]:px-5">
        <div className="px-5 max-[820px]:mb-3 max-[820px]:px-0">
          <a
            className="inline-flex h-10 items-center gap-2 rounded-md px-2 text-sm font-semibold text-slate-500 no-underline transition hover:bg-slate-100 hover:text-slate-950 dark:text-slate-400 dark:hover:bg-zinc-800 dark:hover:text-white"
            href={mainHomeUrl}
          >
            <span aria-hidden="true">←</span>
            <span>Вернуться на главную</span>
          </a>
        </div>

        <div className="grid min-h-0 min-w-0 grid-rows-[auto_1fr] overflow-hidden">
          <div className="mb-3 flex items-center gap-3">
            <label className="min-w-0 flex-1">
              <span className="sr-only">Поиск по играм</span>
              <input
                autoFocus
                className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-950 outline-none transition placeholder:text-slate-400 focus:border-violet-500 focus:ring-3 focus:ring-violet-500/15 dark:border-zinc-700 dark:bg-zinc-950 dark:text-slate-100 dark:placeholder:text-zinc-500"
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск игры"
              />
            </label>
            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500 dark:bg-zinc-800 dark:text-slate-300">
              {filteredApps.length}/{apps.length}
            </span>
          </div>

          <div className="min-h-0 overflow-y-auto">
            {filteredApps.length > 0 ? (
              <div className="grid gap-2">
                {favoriteApps.length > 0 ? (
                  <ul className="m-0 grid list-none gap-1 px-0 py-0 sm:grid-cols-2 lg:grid-cols-3">
                    {favoriteApps.map((app) => (
                      <GameSwitcherAppItem
                        app={app}
                        isCurrent={app.id === currentApp?.id}
                        key={app.id}
                        onClose={onClose}
                      />
                    ))}
                  </ul>
                ) : null}

                {favoriteApps.length > 0 && otherApps.length > 0 ? (
                  <div className="border-t border-slate-200 dark:border-zinc-800" />
                ) : null}

                {otherApps.length > 0 ? (
                  <ul className="m-0 grid list-none gap-1 px-0 py-0 sm:grid-cols-2 lg:grid-cols-3">
                    {otherApps.map((app) => (
                      <GameSwitcherAppItem
                        app={app}
                        isCurrent={app.id === currentApp?.id}
                        key={app.id}
                        onClose={onClose}
                      />
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : (
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-500 dark:border-zinc-800 dark:bg-zinc-950 dark:text-slate-400">
                Игры не найдены
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default GameSwitcherPanel
